/**
 * Student Quiz List Page - Modernized
 * Displays list of quiz assignments for students with modern UI matching Assignments page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiClock, FiAlertCircle, FiPlay, FiEye, FiFileText } from 'react-icons/fi';
import { formatUTCToChicago } from '../../utils/datetime-comprehensive';
import quizApiService from '../../services/quizApi';
import styles from './StudentQuizList.module.css';



function StudentQuizList() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('open');
  const [courseFilter, setCourseFilter] = useState('active');
  const [courses, setCourses] = useState([]);
  const [startingQuizId, setStartingQuizId] = useState(null);

  // Helper functions for status determination
  const isCompleted = (assignment) => {
    return assignment.status === 'completed' || assignment.submitted_at !== null;
  };

  const isOverdue = (assignment) => {
    if (isCompleted(assignment)) return false;
    const dueDate = assignment.assignment_due_at || assignment.quiz_due_at;
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getUiStatus = (assignment) => {
    if (isCompleted(assignment)) return 'completed';
    if (isOverdue(assignment)) return 'overdue';
    return 'open';
  };

  const fetchCourses = useCallback(async () => {
    try {
      const response = await quizApiService.getCourses();
      if (response.success) {
        setCourses(response.courses || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  }, []);

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await quizApiService.getStudentAssignments({ limit: 1000 });
      
      if (response.success && response.data) {
        setAssignments(response.data);
      } else {
        setError('Failed to load quiz assignments');
      }
    } catch (err) {
      console.error('Error fetching quiz assignments:', err);
      setError('Failed to load quiz assignments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
    fetchCourses();
  }, [fetchAssignments, fetchCourses]);

  const getStatusText = (uiStatus) => {
    switch (uiStatus) {
      case 'completed': return 'Completed';
      case 'overdue': return 'Overdue';
      case 'open': return 'Open';
      default: return 'Open';
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'No Due Date';
    // Format: "January 15, 2026 at 6:59 PM" (matching Assignments style)
    return formatUTCToChicago(dateString, `MMMM d, yyyy 'at' h:mm a`);
  };

  const handleStartQuiz = (assignmentId) => {
    if (startingQuizId) return;
    setStartingQuizId(assignmentId);
    navigate(`/student/assignments/${assignmentId}/warning`);
  };

  const visibleCourseIds = new Set(
    assignments
      .map((assignment) => assignment.course_id)
      .filter((courseId) => courseId !== null && courseId !== undefined)
      .map((courseId) => parseInt(courseId))
  );

  const visibleCourses = courses.filter((course) => visibleCourseIds.has(parseInt(course.id)));

  // Determine active courses
  const activeCourses = visibleCourses.filter(course => {
    // Course is active if is_active is true AND (no end_date OR end_date is in the future)
    const isActive = course.is_active === true || course.is_active === 1;
    const hasNoEndDate = !course.end_date || course.end_date === null;
    const endDateInFuture = course.end_date ? new Date(course.end_date) >= new Date() : false;
    return isActive && (hasNoEndDate || endDateInFuture);
  });
  const activeCourseIds = new Set(activeCourses.map(c => c.id));
  const inactiveCourses = visibleCourses.filter(c => !activeCourseIds.has(c.id));

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (assignment.course_name && assignment.course_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const uiStatus = getUiStatus(assignment);
    const matchesFilter = activeFilter === uiStatus;
    
    // Course filtering logic
    let matchesCourse = false;
    if (courseFilter === 'active') {
      // Show only quizzes from active courses
      matchesCourse = assignment.course_id && activeCourseIds.has(parseInt(assignment.course_id));
    } else if (courseFilter === 'all') {
      // Show all quizzes (both active and inactive courses)
      matchesCourse = true;
    } else {
      // Show quizzes from a specific course (active or inactive)
      matchesCourse = courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
    }
    
    return matchesSearch && matchesFilter && matchesCourse;
  });

  // Filter counts should respect the course filter selection
  const assignmentsForCounts = assignments.filter(assignment => {
    if (courseFilter === 'active') {
      return assignment.course_id && activeCourseIds.has(parseInt(assignment.course_id));
    } else if (courseFilter === 'all') {
      return true;
    } else {
      return courseFilter && assignment.course_id && parseInt(assignment.course_id) === parseInt(courseFilter);
    }
  });

  const filterCounts = {
    open: assignmentsForCounts.filter(a => getUiStatus(a) === 'open').length,
    completed: assignmentsForCounts.filter(a => getUiStatus(a) === 'completed').length,
    overdue: assignmentsForCounts.filter(a => getUiStatus(a) === 'overdue').length,
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>My Quizzes</h1>
        </header>
        <div className={styles.loadingState}>
          <FiClock size={48} />
          <h3>Loading quizzes...</h3>
          <p>Please wait while we fetch your quiz assignments.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>My Quizzes</h1>
        </header>
        <div className={styles.errorState}>
          <FiAlertCircle size={48} />
          <h3>Error loading quizzes</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>My Quizzes</h1>
      </header>

      {/* Search and Filter Row */}
      <div className={styles.searchFilterRow}>
        <div className={styles.searchBar}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search quizzes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={styles.courseFilterContainer}>
          <label className={styles.courseFilterLabel}>Course:</label>
          <select
            className={styles.courseFilterSelect}
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
          >
            <option value="active">All Active Courses</option>
            {activeCourses.length === 0 && inactiveCourses.length === 0 && (
              <option value="" disabled>No courses available</option>
            )}
            {activeCourses.length > 0 && activeCourses.map(course => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name}
              </option>
            ))}
            {inactiveCourses.length > 0 && (
              <>
                <option disabled>──────────</option>
                <option disabled>Past Courses</option>
                {inactiveCourses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.code} - {course.name}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
      </div>

      <div className={styles.filters}>
        <button
          className={`${styles.filterPill} ${activeFilter === 'open' ? styles.active : ''}`}
          onClick={() => setActiveFilter('open')}
          data-key="open"
        >
          <span className={styles.count}>{filterCounts.open}</span>
          Open
        </button>
        <button
          className={`${styles.filterPill} ${activeFilter === 'completed' ? styles.active : ''}`}
          onClick={() => setActiveFilter('completed')}
          data-key="completed"
        >
          <span className={styles.count}>{filterCounts.completed}</span>
          Completed
        </button>
        <button
          className={`${styles.filterPill} ${activeFilter === 'overdue' ? styles.active : ''}`}
          onClick={() => setActiveFilter('overdue')}
          data-key="overdue"
        >
          <span className={styles.count}>{filterCounts.overdue}</span>
          Overdue
        </button>
      </div>
{filteredAssignments.length > 0 ? (
  <div className={styles.quizList}>
    {filteredAssignments.map((assignment, index) => (
      <motion.div
        key={assignment.assignment_id}
        className={styles.quizCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <div className={styles.cardBody}>
          <div className={styles.cardInfo}>
            <h3 className={styles.quizTitle}>{assignment.title}</h3>
            <div className={styles.courseInfo}>
              {assignment.course_name || 'No course assigned'}
              {(assignment.instructor_first_name || assignment.instructor_last_name) && (
                <span> • {[assignment.instructor_first_name, assignment.instructor_last_name].filter(Boolean).join(' ')}</span>
              )}
            </div>
            <div className={styles.metaRow}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Due Date:</span>
                <span className={styles.metaValue}>{formatDate(assignment.assignment_due_at || assignment.quiz_due_at)}</span>
              </div>
              <div className={styles.metaDivider}></div>
              <div className={styles.metaItem}>
                <FiClock size={14} />
                <span className={styles.metaValue}>{assignment.time_limit_minutes ? `${assignment.time_limit_minutes} min` : 'No time limit'}</span>
              </div>
              <div className={styles.metaDivider}></div>
              <div className={styles.metaItem}>
                <FiFileText size={14} />
                <span className={styles.metaValue}>{assignment.attempts_allowed} {assignment.attempts_allowed === 1 ? 'attempt' : 'attempts'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.cardFooter}>
          <div className={styles.actions}>
            {!isCompleted(assignment) && !assignment.latest_attempt_id && (
              <button
                className={`${styles.button} ${styles.primary}`}
                onClick={() => handleStartQuiz(assignment.assignment_id)}
                disabled={startingQuizId === assignment.assignment_id}
              >
                <FiPlay />
                {startingQuizId === assignment.assignment_id ? 'Starting...' : 'Start Quiz'}
              </button>
            )}
            {!isCompleted(assignment) && assignment.latest_attempt_id && (
              <button
                className={`${styles.button} ${styles.primary}`}
                onClick={() => navigate(`/student/attempt/${assignment.latest_attempt_id}`)}
              >
                <FiPlay />
                Resume Quiz
              </button>
            )}
            {isCompleted(assignment) && (
              <button
                className={`${styles.button} ${styles.primary}`}
                onClick={() => navigate(`/student/result/${assignment.latest_attempt_id}`)}
              >
                <FiEye />
                View Results
              </button>
            )}
          </div>
          {/* Only show status badge for completed quizzes - open/overdue are already filtered by tabs */}
          {getUiStatus(assignment) === 'completed' && (
            <div className={`${styles.statusBadge} ${styles.completed}`}>
              {getStatusText('completed')}
            </div>
          )}
        </div>
      </motion.div>
    ))}
  </div>
      ) : (
        <div className={styles.emptyState}>
          <FiFileText />
          <h3>No quizzes found</h3>
          <p>
            {searchTerm || activeFilter !== 'open' || courseFilter !== 'active'
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'You don\'t have any quiz assignments yet.'
            }
          </p>
        </div>
      )}
    </div>
  );
}

export default StudentQuizList;
